/* BackupDetailRenderer.js */

"use strict";

const {
    Control: {
        Action: {Button},
        Boolean: {InputCheckbox},
        Textfield: {InputText},
        OptionSelection: {
            Single: {InputRadio},
        },
    },
} = require("@gluenetworks/steve_ui_core");

const _ = require("lodash");

class BackupDetailRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("base-backup-veil-gl");
        this._rootElem.component = this._model;
        this.nameContainer = this._createNameContainer();
        this._devicesContainer = this._createDevicesContainer();
        this._anchorForTooltip = document.createElement("b");
        this._anchorForTooltip.innerHTML = "&nbsp;supported OS&nbsp;";
        this._runCapture = new InputCheckbox({
            label: "Capture new snapshot",
            labelShown: true,
            value: true,
            darkTheme: true,
        });
        this._confirmButton = new Button({
            label: "Start Backup",
            darkTheme: true,
        });
        this._cancelButton = new Button({
            label: "Cancel",
            darkTheme: true,
        });
        this._scheduleButton = new Button({
            label: "Schedule Backup",
            darkTheme: true,
        });

        this._unsupportedDevicesMsg = document.createElement("span");
        this._unsupportedDevicesMsg.classList.add("unsupported-devices-msg");

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = this._model._title;
        this._messageBox = this._createMessageBox();
        this._buttonContainer = this._createButtonContainer();
        this._registerListeners();
    }

    _createNameContainer() {
        const nameContainer = document.createElement("div");
        nameContainer.classList.add("name-container");

        const nameLabel = document.createElement("label");
        nameLabel.innerText = "Name:";

        this._nameField = new InputText({
            darkTheme: true,
            placeholder: "Name",
            value:
                "Backup - " +
                new Date().toLocaleDateString() +
                " " +
                new Date().toLocaleTimeString(),
        });

        nameContainer.appendChild(nameLabel);
        nameContainer.appendChild(this._nameField.renderer.rootElement);

        return nameContainer;
    }

    _createDevicesContainer() {
        const devicesContainer = document.createElement("div");
        devicesContainer.classList.add("devices-container");

        this._devicesLabel = document.createElement("label");
        this._devicesLabel.innerHTML = "Devices:";

        const radioOpts = [{label: "Selection", value: "selection"}];
        if (this._model._hasBasicSearch) {
            radioOpts.push({
                label: "Current search results",
                value: "current_search_results",
            });
        } else {
            radioOpts.push({label: "Apply Current Filter", value: "apply_current_filter"});
        }
        radioOpts.push({label: "All", value: "all"});

        this._devicesSelection = new InputRadio({
            darkTheme: true,
            horizontal: true,
            options: radioOpts,
            value: "selection",
        });

        devicesContainer.appendChild(this._devicesLabel);
        devicesContainer.appendChild(this._devicesSelection.renderer.rootElement);

        return devicesContainer;
    }

    _createMessageBox() {
        const messageStart = document.createElement("p");
        messageStart.innerHTML = `Only devices running a`;
        const messageEnd = document.createElement("p");
        messageEnd.innerHTML = `will be backed up.`;
        const messageFull = document.createElement("div");
        messageFull.classList.add("supported-os-msg-container");
        messageFull.appendChild(messageStart);
        messageFull.appendChild(this._anchorForTooltip);
        messageFull.appendChild(messageEnd);

        return messageFull;
    }

    _createList(array) {
        const uniqueSet = new Set(array);
        const listItems = [...uniqueSet].map((item) => item || "Empty OS");
        return listItems.join("\n");
    }

    _createButtonContainer() {
        const buttonContainer = document.createElement("button-container");
        buttonContainer.appendChild(this._unsupportedDevicesMsg);
        buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
        buttonContainer.appendChild(this._scheduleButton.renderer.rootElement);
        buttonContainer.appendChild(this._confirmButton.renderer.rootElement);
        return buttonContainer;
    }

    _registerListeners() {
        this._confirmButton.on("UserClick", this._onConfirmButtonClick.bind(this));
        this._cancelButton.on("UserClick", this._onCancelButtonClick.bind(this));
        this._devicesSelection.on("UserValue", this._onDeviceSelChange.bind(this));
        this._scheduleButton.on("UserClick", this._schedulePlanAction.bind(this));
        this._nameField.on("UserValue", (this._onNameChange = this._onNameChange.bind(this)));
    }

    _unregisterListeners() {
        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._devicesSelection.removeAllListeners("UserValue");
        this._scheduleButton.removeListener("UserClick", this._schedulePlanAction);
        this._nameField.removeAllListeners("UserValue");
    }

    _onConfirmButtonClick() {
        this._callCreateAction();
    }

    _onCancelButtonClick() {
        this._model.cancelAction();
    }

    _callCreateAction() {
        this._model.backupAction(this._nameField.getValue() || "", this._runCapture.getValue());
    }

    _onDeviceSelChange(val) {
        switch (val) {
            case "all":
                this._model.devices = {};
                break;
            case "current_search_results":
            case "apply_current_filter":
                this._model.devices = this._getFeedFilter();
                break;
            default:
                this._model.devices = this._model._gameEditor._activeView.selectedRows;
                break;
        }
    }

    _getFeedFilter() {
        const feedFilter = this._model._gameEditor._activeView._pagingDeviceFeed._filter;
        if (!_.isEmpty(feedFilter.$userQuery)) {
            return feedFilter.$userQuery;
        } else if (feedFilter.hasOwnProperty("$and") || feedFilter.hasOwnProperty("$or")) {
            return feedFilter;
        }
        return {};
    }

    _schedulePlanAction() {
        const workInput = this._getWorkInput();
        const scheduleName =
            workInput.description ||
            "Device Backup - " +
                new Date().toLocaleDateString() +
                " " +
                new Date().toLocaleTimeString();
        return this._model.scheduleBackup(
            scheduleName,
            workInput,
            this._devicesSelection.getValue()
        );
    }

    _onNameChange(val) {
        if (!val || val.trim().length === 0) {
            this._confirmButton.setDisabled(true);
            this._scheduleButton.setDisabled(true);
        } else {
            this._confirmButton.setDisabled(false);
            this._scheduleButton.setDisabled(false);
        }
    }

    _getWorkInput() {
        return {
            description: this._nameField.getValue(),
            runCapture: this._runCapture.getValue(),
        };
    }

    shutdown() {
        this._unregisterListeners();

        const promArr = [
            this._runCapture.shutdown(),
            this._confirmButton.shutdown(),
            this._cancelButton.shutdown(),
            this._nameField.shutdown(),
        ];

        return Promise.all(promArr).then(() => {
            this._runCapture =
                this._confirmButton =
                this._buttonContainer =
                this._titleComponent =
                this._messageBox =
                this._model =
                this._rootElem =
                this._cancelButton =
                    undefined;
        });
    }

    render() {
        return this.model.getSupportedOs().then(() => {
            if (!this._rootElem.hasChildNodes()) {
                // Create tooltip for supported OS
                const listOfSUpportedOs = this._createList(this.model._supportedOsList);
                this._anchorForTooltip.setAttribute("title", listOfSUpportedOs);

                this._showHideUnsupportedDevices();

                this._rootElem.appendChild(this._titleComponent);
                this._rootElem.appendChild(this._messageBox);

                this._rootElem.appendChild(this._devicesContainer);
                this._rootElem.appendChild(this.nameContainer);
                const runCaptureContainer = document.createElement("div");
                runCaptureContainer.classList.add("run-capture-container");
                runCaptureContainer.appendChild(this._runCapture.renderer.rootElement);
                this._rootElem.appendChild(runCaptureContainer);

                this._rootElem.appendChild(this._buttonContainer);
            }
            return Promise.resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    _showHideUnsupportedDevices() {
        const _unsupportedDevices = this._getUnsupportedDevices(
            this.model._selectedDevices,
            this.model._supportedOsList
        );

        if (_unsupportedDevices.length > 0) {
            this._viewWarningIcon = document.createElement("actionable-icon-gl");
            this._viewWarningIcon.dataset.icon = "common.warning";

            const msgStart = `${_unsupportedDevices.length} device${
                _unsupportedDevices.length === 1 ? "" : "s"
            } run${_unsupportedDevices.length === 1 ? "s" : ""} an`;
            this._unsupportedDevicesMsg.innerHTML = `${this._viewWarningIcon.outerHTML}${msgStart}`;
            const msgUnsupportedOS = document.createElement("span");
            msgUnsupportedOS.innerHTML = "<b>unsupported OS</b>.";
            msgUnsupportedOS.classList.add("os-name-anchor");
            this._unsupportedDevicesMsg.appendChild(msgUnsupportedOS);

            // Create tooltip on the _unsupportedDevicesMsg element
            const listOfSUpportedOs = this._createList(_unsupportedDevices.map((i) => i.os));
            msgUnsupportedOS.setAttribute("title", listOfSUpportedOs);

            this._unsupportedDevicesMsg.classList.remove("hidden");
        } else {
            this._unsupportedDevicesMsg.classList.add("hidden");
        }
    }

    _getUnsupportedDevices(devices, supportedOS) {
        return devices.filter((device) => !supportedOS.includes(device.os));
    }
}

module.exports = BackupDetailRenderer;
